import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-8">
          Privacy Policy
        </h1>
        <p className="text-text-muted font-mono text-sm mb-12">
          Last Updated: {new Date().toLocaleDateString()}
        </p>

        <div className="space-y-8">
          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Our Commitment
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              COUP: The Counterscript is committed to protecting your privacy and the privacy of students. We believe in transparency, 
              minimal data collection, and absolute control over personal information.
            </p>
            <p className="text-text-secondary leading-relaxed">
              <strong className="text-white">We do not sell, rent, or distribute personal data. Period.</strong>
            </p>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              What We Collect
            </h2>
            <div className="space-y-4 text-text-secondary leading-relaxed">
              <div>
                <h3 className="text-white font-display font-semibold mb-2">Teacher/Council Accounts:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Email address (for account authentication)</li>
                  <li>Name (optional, for personalization)</li>
                  <li>School/organization name (optional, for district licensing)</li>
                  <li>Payment information (processed securely through Stripe, not stored on our servers)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-display font-semibold mb-2">Game Data:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Classroom session data (team names, faction assignments)</li>
                  <li>Written submissions and counterscripts (associated with sessions, not individual students)</li>
                  <li>Game scores and analytics (aggregated at the classroom level)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-white font-display font-semibold mb-2">What We Don't Collect:</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Individual student names or personal identifiers</li>
                  <li>Student email addresses</li>
                  <li>Student images or photos</li>
                  <li>Precise geolocation data</li>
                  <li>Social media profiles</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              How We Use Your Data
            </h2>
            <ul className="space-y-3 text-text-secondary leading-relaxed">
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span>To provide and maintain the COUP Data Hub service</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span>To process payments and manage subscriptions</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span>To send service updates and important notifications</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span>To improve our platform based on aggregated usage patterns</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span>To respond to support requests and customer inquiries</span>
              </li>
            </ul>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Data Storage & Security
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              All data is encrypted in transit (HTTPS/TLS) and at rest. We use industry-standard security measures 
              to protect your information, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
              <li>Secure cloud infrastructure (Firebase/Google Cloud)</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication requirements</li>
              <li>Automated backups and disaster recovery procedures</li>
            </ul>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Data Retention
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              We retain account and game data only as long as necessary to provide the service:
            </p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
              <li>Active accounts: Data retained while subscription is active</li>
              <li>Canceled accounts: Data retained for 90 days, then permanently deleted</li>
              <li>You can request immediate deletion of your data by contacting support</li>
            </ul>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Third-Party Services
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              We use select third-party services to operate COUP: The Counterscript:
            </p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
              <li><strong className="text-white">Stripe:</strong> Payment processing (PCI-DSS compliant)</li>
              <li><strong className="text-white">Firebase/Google Cloud:</strong> Hosting and database services</li>
              <li><strong className="text-white">Email service provider:</strong> Transactional emails and updates</li>
            </ul>
            <p className="text-text-secondary leading-relaxed mt-4">
              These providers are bound by strict data protection agreements and are COPPA/FERPA compliant where applicable.
            </p>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Your Rights
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              You have complete control over your data:
            </p>
            <ul className="space-y-3 text-text-secondary leading-relaxed">
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span><strong className="text-white">Access:</strong> Request a copy of all data we store about you</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span><strong className="text-white">Correction:</strong> Update or correct your account information anytime</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span><strong className="text-white">Deletion:</strong> Request permanent deletion of your account and all associated data</span>
              </li>
              <li className="flex items-start">
                <span className="text-copper mr-3">→</span>
                <span><strong className="text-white">Portability:</strong> Export your game data in standard formats</span>
              </li>
            </ul>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              COPPA & FERPA Compliance
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              COUP: The Counterscript is designed to minimize student data collection and comply with COPPA (Children's Online Privacy 
              Protection Act) and FERPA (Family Educational Rights and Privacy Act):
            </p>
            <ul className="list-disc list-inside space-y-2 text-text-secondary ml-4">
              <li>Students do not create individual accounts</li>
              <li>No personal student information is collected or stored</li>
              <li>Game data is associated with classroom sessions, not individuals</li>
              <li>Teachers maintain full control over all classroom data</li>
            </ul>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Changes to This Policy
            </h2>
            <p className="text-text-secondary leading-relaxed">
              We may update this privacy policy to reflect changes in our practices or legal requirements. Material 
              changes will be communicated via email to account holders. Continued use of COUP: The Counterscript after policy updates 
              constitutes acceptance of the new terms.
            </p>
          </section>

          <section className="card-tactical">
            <h2 className="text-2xl font-display font-bold text-white mb-4">
              Contact Us
            </h2>
            <p className="text-text-secondary leading-relaxed mb-4">
              Questions about this privacy policy or your data?
            </p>
            <p className="text-text-secondary">
              Email: <a href="mailto:privacy@counterscripthq.com" className="text-copper hover:text-white transition-colors font-mono">
                privacy@counterscripthq.com
              </a>
            </p>
            <p className="text-text-secondary mt-2">
              Or use our <a href="/contact" className="text-copper hover:text-white transition-colors">contact form</a>
            </p>
          </section>

          <div className="border-t-2 border-tactical-frame pt-8 mt-12">
            <p className="text-text-muted text-center font-mono text-sm">
              CounterScriptHQ is committed to protecting your privacy and is operated by Dreadnought Juice Press LLC
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
